import React from 'react';

export const ORG_ROLE_LABELS = {
  organization_head: 'Organization Head',
  admin: 'Admin',
  finance_manager: 'Finance Manager',
  accountant: 'Accountant',
  project_manager: 'Project Manager',
  employee: 'Employee',
  viewer: 'Viewer',
};

export const GLOBAL_ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'User',
};

const ORG_ROLE_STYLES = {
  organization_head: { background: '#fde7ef', color: '#9f1239' },
  admin: { background: '#fff1cc', color: '#92400e' },
  finance_manager: { background: '#dcfce7', color: '#166534' },
  accountant: { background: '#dbeafe', color: '#1d4ed8' },
  project_manager: { background: '#ede9fe', color: '#6d28d9' },
  employee: { background: '#f1f5f9', color: '#334155' },
  viewer: { background: '#ecfeff', color: '#0f766e' },
};

const GLOBAL_ROLE_STYLES = {
  super_admin: { background: '#fee2e2', color: '#b91c1c' },
  admin: { background: '#fff1cc', color: '#92400e' },
  user: { background: '#f1f5f9', color: '#334155' },
};

const STATUS_STYLES = {
  active: { background: '#dcfce7', color: '#166534' },
  suspended: { background: '#fee2e2', color: '#b91c1c' },
  inactive: { background: '#e2e8f0', color: '#334155' },
  pending: { background: '#fef3c7', color: '#92400e' },
  blocked: { background: '#fee2e2', color: '#b91c1c' },
  open: { background: '#ede9fe', color: '#6d28d9' },
  investigating: { background: '#dbeafe', color: '#1d4ed8' },
  resolved: { background: '#dcfce7', color: '#166534' },
  success: { background: '#dcfce7', color: '#166534' },
  failed: { background: '#fee2e2', color: '#b91c1c' },
};

const SEVERITY_STYLES = {
  low: { background: '#f1f5f9', color: '#334155' },
  medium: { background: '#dbeafe', color: '#1d4ed8' },
  high: { background: '#fed7aa', color: '#c2410c' },
  critical: { background: '#fee2e2', color: '#b91c1c' },
};

export function formatOrgRole(role) {
  return ORG_ROLE_LABELS[role] || 'Unassigned';
}

export function formatGlobalRole(role) {
  return GLOBAL_ROLE_LABELS[role] || 'User';
}

export function formatDateTime(value) {
  if (!value) return 'Never';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
}

export function Chip({ label, tone = 'neutral', style = {} }) {
  const fallback = STATUS_STYLES[tone] || { background: '#f1f5f9', color: '#334155' };
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap"
      style={{ ...fallback, ...style }}
    >
      {label}
    </span>
  );
}

export function OrgRoleChip({ role }) {
  return <Chip label={formatOrgRole(role)} style={ORG_ROLE_STYLES[role] || ORG_ROLE_STYLES.employee} />;
}

export function GlobalRoleChip({ role }) {
  return <Chip label={formatGlobalRole(role)} style={GLOBAL_ROLE_STYLES[role] || GLOBAL_ROLE_STYLES.user} />;
}

export function StatusChip({ status }) {
  const normalized = String(status || 'inactive').toLowerCase();
  return <Chip label={normalized.replace(/_/g, ' ')} tone={normalized} />;
}

export function SeverityChip({ severity }) {
  const normalized = String(severity || 'low').toLowerCase();
  return <Chip label={normalized} style={SEVERITY_STYLES[normalized] || SEVERITY_STYLES.low} />;
}

export function ActionIconButton({ icon: Icon, label, onClick, tone = 'default', disabled = false }) {
  const palette = {
    default: { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' },
    view: { background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
    edit: { background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' },
    delete: { background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' },
    success: { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' },
  }[tone] || { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' };

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
      style={palette}
    >
      <Icon size={15} />
    </button>
  );
}

export function SuperAdminPanel({ open, title, subtitle, onClose, children, footer }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(15,23,42,0.42)', backdropFilter: 'blur(4px)' }}>
      <button type="button" aria-label="Close panel" className="flex-1 cursor-default" onClick={onClose} />
      <section className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl">
        <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900">{title}</h3>
              {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
            >
              Close
            </button>
          </div>
        </header>
        <div className="p-5">{children}</div>
        {footer ? <footer className="sticky bottom-0 border-t border-slate-100 bg-white px-5 py-4">{footer}</footer> : null}
      </section>
    </div>
  );
}

export function exportRowsAsCsv(rows, columns, fileName) {
  const header = columns.map((column) => column.label);
  const data = rows.map((row) => columns.map((column) => {
    const raw = column.value ? column.value(row) : row[column.key];
    const value = raw == null ? '' : String(raw).replace(/"/g, '""');
    return `"${value}"`;
  }));

  const csv = [header.join(','), ...data.map((line) => line.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}